import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getDashboardBookings,
  getDashBoardCount,
  getDashBoardFollowups,
  getDashboardInquiries,
  getDashboardLeads,
  getDashboardQuotations,
  getDashboardSeemoreFollowups,
} from "./services/dashboard.services";
import { IDashboardCount, IDashboardState, IFollowup } from "./dashboardModel";
import { ITourBooking } from "../tourbooking/tourBookingModel";
import { IQuotation } from "../quotation/quotationModel";
import { ILead } from "../lead/leadModel";
import { IInquiry } from "../inquiry/inquiryModel";
import { updateFollowUpStatus } from "../inquiry/services/inquiry.services";

const initialState: IDashboardState = {
  loading: false,
  followUpLoading: false,
  newLoading: false,
  error: "",
  sessionDetails: {},
  dateFilter: {
    SelectedDate: null,
    FromDate: null,
    ToDate: null,
  },
  followupId: null,
  activeTabIndex: 0,
  filterFollowupsStageID: 0,
  DashBoardCountList: [],
  Followups: [],
  DashboardBookingList: [],
  DashboardQuotationList: [],
  DashboardLeadList: [],
  DashboardInquiryList: [],
  DashboardInquiryFollowups: [],
  DashboardLeadFollowups: [],
  DashboardSeeMoreInquiryFollowups: [],
  DashboardSeeMoreLeadFollowups: [],
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearSessionDetails: (state) => {
      state.sessionDetails = initialState.sessionDetails;
    },
    clearDateFilter: (state) => {
      state.dateFilter = initialState.dateFilter;
    },
    setDateFilter: (state, action) => {
      state.dateFilter = action.payload;
    },
    clearFolloupID: (state) => {
      state.followupId = initialState.followupId;
    },
    setFolloupID: (state, action) => {
      state.followupId = action.payload;
    },
    setActiveTabIndex: (state, action) => {
      state.activeTabIndex = action.payload;
    },
    setfilterFollowupsStageID: (state, action) => {
      state.filterFollowupsStageID = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateFollowUpStatus.pending, (state) => {
      state.followUpLoading = true;
    });
    builder.addCase(updateFollowUpStatus.fulfilled, (state) => {
      state.followUpLoading = false;
      state.error = "";
    });
    builder.addCase(updateFollowUpStatus.rejected, (state, action) => {
      state.followUpLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getDashBoardCount.pending, (state) => {
      state.newLoading = true;
      state.error = "";
    });
    builder.addCase(
      getDashBoardCount.fulfilled,
      (state, action: PayloadAction<IDashboardCount[]>) => {
        state.newLoading = false;
        state.DashBoardCountList = action.payload || [];
      }
    );
    builder.addCase(getDashBoardCount.rejected, (state, action) => {
      state.newLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashBoardCountList = initialState.DashBoardCountList;
    });
    builder.addCase(getDashBoardFollowups.pending, (state) => {
      state.followUpLoading = true;
      state.error = "";
    });
    builder.addCase(
      getDashBoardFollowups.fulfilled,
      (
        state,
        action: PayloadAction<{
          InquiryList: IFollowup[];
          LeadList: IFollowup[];
        }>
      ) => {
        state.followUpLoading = false;
        state.DashboardInquiryFollowups = action.payload?.InquiryList || [];
        state.DashboardLeadFollowups = action.payload?.LeadList || [];
      }
    );
    builder.addCase(getDashBoardFollowups.rejected, (state, action) => {
      state.followUpLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardInquiryFollowups = initialState.DashboardInquiryFollowups;
      state.DashboardLeadFollowups = initialState.DashboardLeadFollowups;
    });
    builder.addCase(getDashboardBookings.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardBookings.fulfilled,
      (state, action: PayloadAction<ITourBooking[]>) => {
        state.loading = false;
        state.DashboardBookingList = action.payload || [];
      }
    );
    builder.addCase(getDashboardBookings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardBookingList = [];
    });
    builder.addCase(getDashboardQuotations.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardQuotations.fulfilled,
      (state, action: PayloadAction<IQuotation[]>) => {
        state.loading = false;
        state.DashboardQuotationList = action.payload || [];
      }
    );
    builder.addCase(getDashboardQuotations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardQuotationList = [];
    });
    builder.addCase(getDashboardLeads.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardLeads.fulfilled,
      (state, action: PayloadAction<ILead[]>) => {
        state.loading = false;
        state.DashboardLeadList = action.payload || [];
      }
    );
    builder.addCase(getDashboardLeads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardLeadList = [];
    });
    builder.addCase(getDashboardInquiries.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardInquiries.fulfilled,
      (state, action: PayloadAction<IInquiry[]>) => {
        state.loading = false;
        state.DashboardInquiryList = action.payload || [];
      }
    );
    builder.addCase(getDashboardInquiries.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardInquiryList = [];
    });
    builder.addCase(getDashboardSeemoreFollowups.pending, (state) => {
      state.followUpLoading = true;
      state.error = "";
    });
    builder.addCase(
      getDashboardSeemoreFollowups.fulfilled,
      (
        state,
        action: PayloadAction<{
          InquiryList: IFollowup[];
          LeadList: IFollowup[];
        }>
      ) => {
        state.followUpLoading = false;
        state.DashboardSeeMoreInquiryFollowups =
          action.payload?.InquiryList || [];
        state.DashboardSeeMoreLeadFollowups = action.payload?.LeadList || [];
      }
    );
    builder.addCase(getDashboardSeemoreFollowups.rejected, (state, action) => {
      state.followUpLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.DashboardSeeMoreInquiryFollowups =
        initialState.DashboardSeeMoreInquiryFollowups;
      state.DashboardSeeMoreLeadFollowups =
        initialState.DashboardSeeMoreLeadFollowups;
    });
  },
});

export const {
  clearSessionDetails,
  clearDateFilter,
  setDateFilter,
  setActiveTabIndex,
  setfilterFollowupsStageID,
  clearFolloupID,
  setFolloupID,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
